import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import CustomBillingAddressTooltip from 'src/components/CustomBillingAddressTooltip/CustomBillingAddressTooltip'
import RadioItems from 'src/components/RadioItems/RadioItems'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { CountryFieldModel } from 'src/models/country.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

export type CustomVirtualCardAddressFlatFields = {
  country?: string
  address1?: string
  address2?: string
  city?: string
  province?: string
  postal?: string
}

interface CustomVirtualCardAddressBoxProps {
  currentBillingAddress: React.ReactElement
  customBillingAddress?: boolean
  onButtonSelect: (val: boolean) => void
  countrySchema: CountryFieldModel[]
  flatFields: CustomVirtualCardAddressFlatFields
  flatFieldsErrors: Partial<{
    [field in keyof CustomVirtualCardAddressFlatFields]: Array<
      React.ReactElement | string
    >
  }>
  onFlatFieldChange: (
    fieldName: keyof CustomVirtualCardAddressFlatFields,
    value: CustomVirtualCardAddressFlatFields[keyof CustomVirtualCardAddressFlatFields],
  ) => void
}

const CustomVirtualCardAddressBox: React.FC<
  CustomVirtualCardAddressBoxProps
> = (props): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <RadioItems
        fieldLabel={<Trns source={'Virtual Card Billing Address'} />}
        alignStart
        noBorder
        staticOnHover
        tooltip={<CustomBillingAddressTooltip openLeft={false} />}
        items={[
          <div
            key={0}
            css={css`
              display: flex;
              flex-direction: column;
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              ${!!props.customBillingAddress &&
              css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            <span>
              <Trns source={'Source Billing Address:'} />
            </span>
            <span>{props.currentBillingAddress}</span>
          </div>,
          <form
            key={1}
            css={css`
              display: flex;
              flex-direction: column;
              flex-grow: 2;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                ${!!props.customBillingAddress &&
                css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              `}
            >
              <Trns source={'Change Billing Address'} />
            </span>
            {props.customBillingAddress &&
              props.countrySchema.map((field, i) => (
                <Fragment key={i}>
                  {field.options !== undefined && (
                    <TextDropdown
                      css={css`
                        margin-top: 30px;
                      `}
                      errors={props.flatFieldsErrors[field.name]}
                      placeholder={translateSingleString(
                        locale,
                        'Select ${fieldLabel}',
                        { fieldLabel: field.label },
                      )}
                      fieldLabel={field.label}
                      selectedValues={
                        props.flatFields[field.name] !== undefined
                          ? new Set([props.flatFields[field.name] as string])
                          : new Set([])
                      }
                      chooseOption={(val: string): void => {
                        props.onFlatFieldChange(field.name, val)
                      }}
                      optionGroups={[
                        {
                          options: field.options.map((opt) => ({
                            value: opt.code,
                            details: {
                              text: opt.label,
                            },
                          })),
                        },
                      ]}
                    />
                  )}
                  {field.options === undefined && (
                    <TextInputField
                      css={css`
                        margin-top: 30px;
                      `}
                      errors={props.flatFieldsErrors[field.name]}
                      design={TextInputFieldDesigns.New}
                      fieldLabel={field.label}
                      value={props.flatFields[field.name]}
                      onChange={(val: string): void => {
                        props.onFlatFieldChange(field.name, val)
                      }}
                      placeholder={translateSingleString(
                        locale,
                        'Enter ${fieldLabel}',
                        { fieldLabel: field.label },
                      )}
                    />
                  )}
                </Fragment>
              ))}
          </form>,
        ]}
        currentIndex={props.customBillingAddress ? 1 : 0}
        setIndex={(val: number): void => {
          props.onButtonSelect(val === 1)
        }}
      />
    </Box>
  )
}

export default CustomVirtualCardAddressBox
